/**
 * <upsell-recommendations form="<product-form-id>">
 *
 * Keeps metafield-driven product recommendations in sync with the selected variant.
 *
 * The theme already fetches the whole product section on every variant change and hands the
 * parsed markup to listeners via the form's `product:rerender` event (see ProductRerender in
 * theme.js). Its partial-rerender whitelist covers price, buy-buttons, inventory and friends but
 * not the recommendation block, so this element picks its own freshly rendered counterpart out of
 * that same fragment. No extra request, and the server stays the single source of truth for which
 * recommendations belong to which variant.
 */
class UpsellRecommendations extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const form = document.forms[this.getAttribute('form')];
    if (!form) return;
    form.addEventListener('product:rerender', this._onRerender.bind(this), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  _onRerender(event) {
    var fragment = event.detail && event.detail.htmlFragment;
    if (!fragment || !this.id) return;

    var selector = '#' + (window.CSS && CSS.escape ? CSS.escape(this.id) : this.id);
    var fresh = null;
    try {
      fresh = fragment.querySelector ? fragment.querySelector(selector) : null;
      // The fragment may nest the section inside a template element, mirroring how theme.js
      // reaches into it with its own deep lookup.
      if (!fresh && fragment.querySelector) {
        var host = fragment.querySelector('template');
        if (host && host.content) fresh = host.content.querySelector(selector);
      }
    } catch (_) {
      return;
    }
    if (!fresh) return;

    // Replacing the children (not the element) keeps this element — and therefore its listener —
    // alive across variant changes.
    this.innerHTML = fresh.innerHTML;
  }
}

if (!window.customElements.get('upsell-recommendations')) {
  window.customElements.define('upsell-recommendations', UpsellRecommendations);
}
